import { useCallback, useRef, useState } from 'react';
import { CheckCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import { MobileBottomSheet } from 'components/generic/MobileBottomSheet.component';
import useIsMobileViewport from 'hooks/useIsMobileViewport.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';

import CN from './RulesSelect.module.scss';

export const RulesSelect = ({ testId, value, onChange, options }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const isMobile = useIsMobileViewport();
  const { val: isOpen, tip: toggle } = useTip(false);
  const ref = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottomLeft');

  const currentOption = options.find((opt) => opt.value === value);

  const handleSelect = (optValue) => {
    onChange(optValue);
    toggle();
  };

  const handleToggle = useCallback(() => {
    if (!isOpen && !isMobile && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownPosition(window.innerHeight - rect.bottom < 300 ? 'top' : 'bottomLeft');
    }
    toggle();
  }, [isOpen, isMobile, toggle]);

  const optionsList = (
    <div data-bem="RulesSelect__options" data-testid={testId ? `${testId}-options` : undefined} className={cn(CN.optionsList, { [CN.darkMode]: isDarkMode })} role="listbox">
      {options.map((opt) => (
        <div
          key={opt.value}
          data-bem="RulesSelect__option"
          data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
          className={CN.option}
          role="option"
          aria-selected={opt.value === value}
          tabIndex={opt.value === value ? -1 : 0}
          onClick={() => handleSelect(opt.value)}
        >
          <span className={CN.optionLabel}>{t(opt.labelKey)}</span>
          <span className={CN.radio} aria-hidden="true">
            {opt.value === value ? <CheckCircleIcon className={CN.checkCircleIcon} /> : <div className={CN.unselectedCircle} />}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div ref={ref} data-bem="RulesSelect" data-testid={testId} className={cn(CN.component, { [CN.darkMode]: isDarkMode })}>
      <button data-bem="RulesSelect__trigger" data-testid={testId} className={CN.trigger} type="button" onClick={handleToggle} aria-expanded={isOpen}>
        <span className={CN.triggerLabel}>{currentOption ? t(currentOption.labelKey) : ''}</span>
        <ChevronDownIcon className={CN.chevronDownIcon} />
      </button>

      {isMobile ? (
        <MobileBottomSheet isOpen={isOpen} onClose={toggle}>
          {optionsList}
        </MobileBottomSheet>
      ) : (
        isOpen && (
          <DropdownFoldout width="full" position={dropdownPosition} onClickOutside={toggle} refFromProps={ref}>
            <DropdownFoldout.Body>{optionsList}</DropdownFoldout.Body>
          </DropdownFoldout>
        )
      )}
    </div>
  );
};
